import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import ctl from '@netlify/classnames-template-literals'
import { motion } from 'motion/react'
import { Fragment, ReactNode, Ref, useImperativeHandle, useRef, useState } from 'react'
import { EditorErrorBoundary } from '../error-handling'

export type EditorAccordionPanel = {
	id: string
	render: () => ReactNode
	title: string
}

export type EditorAccordionRef = {
	moveToDetailsStep: () => void
	moveToEditStep: () => void
	moveToReviewStep: () => void
}

export type EditorShellAccordionProps = {
	panels: Array<EditorAccordionPanel>
	defaultPanelId?: string
	ref: Ref<EditorAccordionRef>
	enabledPanelIds?: Array<string>
	campaignId?: string
}

export function EditorShellAccordion({
	panels,
	defaultPanelId,
	ref,
	enabledPanelIds = [],
	campaignId,
}: EditorShellAccordionProps) {
	const [openPanelId, setOpenPanelId] = useState<string | undefined>(defaultPanelId)
	const organisationId = useJobillaOrgId()
	const openPanelIdRef = useRef(openPanelId)
	openPanelIdRef.current = openPanelId

	const changePanel = (nextPanelId: string, source: 'click' | 'programmatic') => {
		logger.track('Editor: Step changed', {
			data: {
				from_step: openPanelIdRef.current,
				to_step: nextPanelId,
				source,
				campaign_id: campaignId,
				organisation_id: organisationId,
			},
		})
		setOpenPanelId(nextPanelId)
	}

	useImperativeHandle(ref, () => ({
		moveToDetailsStep: () => {
			changePanel('details', 'programmatic')
		},
		moveToEditStep: () => {
			changePanel('edit', 'programmatic')
		},
		moveToReviewStep: () => {
			changePanel('review', 'programmatic')
		},
	}))

	const handlePanelClick = (panelId: string) => {
		// Prevent navigation to disabled steps
		if (!enabledPanelIds.includes(panelId)) {
			return
		}

		let nextPanelId: string | undefined

		if (openPanelId === panelId) {
			// Already open, find next panel
			const currentIndex = panels.findIndex((p) => p.id === panelId)
			const nextIndex = currentIndex + 1
			if (nextIndex < panels.length) {
				const nextPanel = panels[nextIndex]
				// Only navigate to next panel if it's enabled
				if (enabledPanelIds.includes(nextPanel.id)) {
					nextPanelId = nextPanel.id
				}
			}
			// If last panel or next panel is disabled, do nothing (stay open)
		} else {
			// Open the clicked panel
			nextPanelId = panelId
		}

		if (nextPanelId) {
			changePanel(nextPanelId, 'click')
		}
	}

	return (
		<div className="min-h-0 h-full flex flex-col">
			{panels.map((panel, i) => (
				<Fragment key={panel.id}>
					<div className="outline-1 outline-grey-100 bg-white min-h-content">
						<div className="flex items-center gap-2">
							<button
								disabled={!enabledPanelIds.includes(panel.id)}
								onClick={() => handlePanelClick(panel.id)}
								className={ctl(`
									w-full text-left text-grey-900 py-3 px-6 flex items-center gap-2
									${
									!enabledPanelIds.includes(panel.id)
										? 'opacity-50 cursor-not-allowed'
										: 'cursor-pointer'
								}
									`)}
							>
								<div className="text-grey-800 font-light border-grey-200 border rounded-full w-5 h-5 text-sm flex items-center justify-center">
									{i + 1}
								</div>
								{panel.title}
							</button>
						</div>
					</div>

					{/* Todo: fix with a smoother animation by measuring screen size and children */}
					<motion.div
						initial={openPanelId === panel.id ? {} : { height: '0px' }}
						animate={openPanelId === panel.id ? {} : { height: '0px' }}
						transition={{ duration: 0.5, type: 'spring', bounce: 0 }}
						className={ctl(`
							bg-white relative overflow-scroll mb-[1px]
							${openPanelId === panel.id ? 'flex-1 min-h-0' : 'h-0'}
						`)}
					>
						<div
							className={ctl(`
							px-6 pb-4
							${openPanelId === panel.id ? 'h-full flex flex-col' : ''}
						`)}
						>
							<EditorErrorBoundary debugId={`${panel.id}-panel`}>
								{panel.render()}
							</EditorErrorBoundary>
						</div>
					</motion.div>
				</Fragment>
			))}
		</div>
	)
}
